import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Phone, Sofa, Monitor, BedDouble, Home, Warehouse, Car, Building2, Trash2,
  TreePine, Briefcase, Hammer, Fence, Bath, Truck, ChevronRight
} from 'lucide-react';

const services = [
  {
    icon: Sofa,
    name: 'Furniture Removal',
    description: 'Get rid of old, broken, or unwanted furniture quickly and hassle-free.',
    examples: ['Couches & sofas', 'Recliners & chairs', 'Tables & desks', 'Dressers & cabinets', 'Bed frames', 'Bookshelves'],
    image: '/services/Furniture Removal.jpg',
  },
  {
    icon: Monitor,
    name: 'Appliance Removal',
    description: 'We safely remove and properly dispose of old appliances large and small.',
    examples: ['Refrigerators', 'Washers & dryers', 'Dishwashers', 'Ovens & stoves', 'Microwaves', 'Small appliances'],
    image: '/services/Appliance Removal.jpg',
  },
  {
    icon: BedDouble,
    name: 'Mattress Removal',
    description: 'Old mattresses are bulky and hard to dispose of. Let us handle it.',
    examples: ['Mattresses', 'Box springs', 'Bed sets', 'Foam toppers', 'Old pillows', 'Sleep furniture'],
    image: '/services/Mattress Removal.jpg',
  },
  {
    icon: Home,
    name: 'Home Cleanouts',
    description: 'Complete home cleanout services for any situation.',
    examples: ['Whole house cleanouts', 'Move out cleaning', 'Estate cleanouts', 'Downsizing support', 'Hoarder cleanouts', 'Tenant cleanouts'],
    image: 'https://customer-assets-lqy194kg.emergentagent.net/job_clearout-pro/artifacts/3nky9o5v_Storage%20Unit%20Cleanout.webp',
    rotate: true,
  },
  {
    icon: Warehouse,
    name: 'Barn Cleanouts',
    description: 'Clear out years of accumulated junk from barns and outbuildings.',
    examples: ['Old storage removal', 'Junk piles', 'Scrap hauling', 'Farm equipment', 'Hay & debris', 'General farm cleanup'],
    image: '/services/Barn Cleanout.jpg',
  },
  {
    icon: Car,
    name: 'Garage Cleanouts',
    description: 'Reclaim your garage space with our thorough cleanout service.',
    examples: ['Junk piles', 'Old shelving', 'Broken tools', 'Paint cans', 'Holiday decorations', 'General clutter'],
    image: '/services/Garage Cleanout.jpg',
  },
  {
    icon: Home,
    name: 'Attic & Basement Cleanouts',
    description: 'Clear out hard-to-reach spaces that have collected years of stuff.',
    examples: ['Stored boxes', 'Old furniture', 'Damaged items', 'Forgotten clutter', 'Seasonal items', 'Old electronics'],
    image: '/services/Basement Cleanout.jpg',
  },
  {
    icon: Building2,
    name: 'Foreclosure Cleanouts',
    description: 'Fast property cleanouts for banks, realtors, and property managers.',
    examples: ['Abandoned property cleanup', 'Full junk removal', 'Debris hauling', 'Quick turnover service', 'Damage cleanup', 'Property prep'],
    image: '/services/Forclosure Cleanout.jpg',
  },
  {
    icon: Trash2,
    name: 'Debris Removal',
    description: 'Construction and renovation debris hauled away quickly.',
    examples: ['Construction debris', 'Renovation waste', 'Lumber & wood', 'Drywall', 'Scrap material', 'General haul off'],
    image: 'https://customer-assets-lqy194kg.emergentagent.net/job_clearout-pro/artifacts/8aojc45i_Debris%20Removal.webp',
  },
  {
    icon: TreePine,
    name: 'Yard Cleanup',
    description: 'Clear brush, limbs, and yard waste to restore your outdoor space.',
    examples: ['Tree limbs & branches', 'Brush piles', 'Bagged yard waste', 'Storm cleanup', 'Outdoor junk', 'Garden debris'],
    image: 'https://customer-assets-lqy194kg.emergentagent.net/job_clearout-pro/artifacts/ouqeqiki_Yard%20Cleanup.webp',
    rotate: true,
  },
  {
    icon: Briefcase,
    name: 'Office Cleanouts',
    description: 'Professional office and commercial space cleanout services.',
    examples: ['Office desks', 'Chairs & furniture', 'Old electronics', 'Filing cabinets', 'Shelving units', 'Business equipment'],
    image: '/services/Office Cleanout.png',
  },
  {
    icon: Hammer,
    name: 'Interior & Exterior Demolition',
    description: 'Light demolition services with complete debris removal.',
    examples: ['Shed tear downs', 'Small structures', 'Interior tear outs', 'Non-structural removal', 'Deck demolition', 'Debris hauling'],
    image: 'https://customer-assets-lqy194kg.emergentagent.net/job_clearout-pro/artifacts/ranw8c7t_Demolition.webp',
    rotate: true,
  },
  {
    icon: Fence,
    name: 'Deck Removal',
    description: 'Safe deck tear down, breakdown, and complete haul away.',
    examples: ['Full deck removal', 'Partial tear downs', 'Rotten wood removal', 'Railing removal', 'Post extraction', 'Complete cleanup'],
    image: '/services/Deck Removal.jpg',
  },
  {
    icon: Bath,
    name: 'Hot Tub Removal',
    description: 'Hot tubs are heavy and awkward. We have the equipment to handle it.',
    examples: ['Hot tub breakdown', 'Spa removal', 'Heavy lift service', 'Disposal included', 'Deck protection', 'Complete haul away'],
    image: '/services/Hot Tub Removal.webp',
  },
  {
    icon: Truck,
    name: 'Dump Trailer Rental',
    description: 'Need to handle it yourself? Rent our dump trailer for your project.',
    examples: ['Cleanup projects', 'Remodel jobs', 'Property cleanups', 'DIY renovations', 'Flexible rental terms', 'Delivery available'],
    image: '/services/Dump Trailer Rental.jpg',
  },
];

export default function ServicesPage() {
  return (
    <div data-testid="services-page">
      {/* Hero with Gradient */}
      <section className="relative py-20 md:py-28 overflow-hidden" data-testid="services-hero">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1a1a1a] via-[#2d2d2d] to-[#1a1a1a]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(220,38,38,0.15)_0%,_transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_rgba(220,38,38,0.1)_0%,_transparent_50%)]" />
        <div className="relative max-w-7xl mx-auto px-4 md:px-12">
          <h1 className="font-['Barlow_Condensed'] text-5xl md:text-7xl font-bold text-white mb-6">
            Junk Removal Services
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl">
            Fast, professional junk and debris removal for homeowners, businesses, rental properties, construction sites, and more across Northeast Oklahoma.
          </p>
        </div>
      </section>

      {/* Services Grid with Images */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-12">
          <div className="space-y-20">
            {services.map((service, index) => (
              <div 
                key={index}
                id={service.name.toLowerCase().replace(/\s+/g, '-').replace(/&/g, 'and')}
                className="grid lg:grid-cols-2 gap-8 items-center"
                data-testid={`service-section-${index}`}
              >
                <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-14 h-14 bg-[#dc2626] rounded-xl flex items-center justify-center">
                      <service.icon className="w-7 h-7 text-white" />
                    </div>
                    <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-bold text-[#1a1a1a]">
                      {service.name}
                    </h2>
                  </div>
                  <p className="text-gray-600 text-lg mb-6">
                    {service.description}
                  </p>
                  <div className="bg-[#f5f5f5] p-6 rounded-xl">
                    <h3 className="font-semibold text-[#1a1a1a] mb-4 text-sm uppercase tracking-wide">
                      Examples:
                    </h3>
                    <div className="grid grid-cols-2 gap-2">
                      {service.examples.map((example, i) => (
                        <div key={i} className="flex items-center gap-2 text-gray-700 text-sm">
                          <ChevronRight className="w-4 h-4 text-[#dc2626] flex-shrink-0" />
                          <span>{example}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <div className="aspect-video rounded-xl overflow-hidden shadow-lg">
                    <img 
                      src={service.image}
                      alt={service.name}
                      className={`w-full h-full object-cover hover:scale-105 transition-transform duration-500 ${service.rotate ? 'rotate-90 scale-[1.8]' : ''}`}
                      loading="lazy"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="relative py-20 md:py-28 overflow-hidden" data-testid="services-cta">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1a1a1a] via-[#2d2d2d] to-[#1a1a1a]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(220,38,38,0.2)_0%,_transparent_60%)]" />
        <div className="relative max-w-4xl mx-auto px-4 md:px-12 text-center">
          <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Clear It Out?
          </h2>
          <p className="text-gray-400 text-lg mb-10">
            Call now or request your free estimate today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-[#dc2626] hover:bg-[#b91c1c] text-white font-bold uppercase tracking-wider py-6 px-8 btn-press" data-testid="services-estimate-btn">
                Request a Free Estimate
              </Button>
            </Link>
            <a href="tel:9183210654">
              <Button variant="outline" className="bg-transparent border-2 border-white text-white hover:bg-white/10 font-bold uppercase tracking-wider py-6 px-8 btn-press" data-testid="services-call-btn">
                <Phone className="w-5 h-5 mr-2" />
                Call (918) 321-0654
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
